import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './styles.module.scss';
import Button from '@components/common/button';
import Typography from '@components/common/typography';
import { useNavigationContext } from '@hooks/context/useNavigationContext';
import { Icon } from '@components/icons';
import Info from '@components/common/info';
import CircleDiagram from '@components/common/circle_diagram';
import illustrationDesktop from '@svg/illustrations/loyalty_banner_desktop.svg';
import illustrationResponsive from '@svg/illustrations/loyalty_banner_responsive.svg';
import useResponsive from '@hooks/helpers/useResponsive';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiAccountService } from '@api';
import { fixedNumbers, formatDate, getLoyaltyLevelString } from '@utils/index';
import { formatFeeRate } from '@utils/payment';
import { useUserContext } from '@hooks/context/useUserContext';
import { useLocales } from '@hooks/helpers/useLocales';
import { dateDifferenceInDays } from '@utils/date';
import { ELoyaltyLevel } from '@xeppt/xeppt-sdk/types/loyalty';
import LoyaltyGraphicDesktop from '@components/illustrations/loyalty_graphic_desktop';
import LoyaltyGraphicResponsive from '@components/illustrations/loyalty_graphic_responsive';
import { EFundsOperation } from '@enum';
import { EProductName } from '@xeppt/xeppt-sdk/types/products';
import { EUserType, TTransactionMethodTypeAction } from '@xeppt/xeppt-sdk/types';
import { routes } from '@const/routes';
import inviteIllustration from '@svg/illustrations/celebrate_business_success.svg';

const LoyaltyLayout = () => {
    const { handleBack } = useNavigationContext();
    const navigate = useNavigate();
    const { windowWidth } = useResponsive();
    const { submitLocale } = useLocales();
    const { loyalty, products, user } = useUserContext();

    const { data: summary } = useApiQuery({
        method: () => apiAccountService.getMyLoyaltySummary()
    });

    const points_to_gold = useMemo(() => {
        const threshold = loyalty?.[`points_to_${ELoyaltyLevel.B}`] || 0;
        return Math.max(0, threshold - (loyalty?.points || 0));
    }, [loyalty]);

    const points_to_platinum = useMemo(() => {
        const threshold = loyalty?.[`points_to_${ELoyaltyLevel.C}`] || 0;
        return Math.max(0, threshold - (loyalty?.points || 0));
    }, [loyalty]);

    const percentToGold = useMemo(() => {
        const threshold = loyalty?.[`points_to_${ELoyaltyLevel.B}`] || 0;
        if (!threshold) return 0;
        return Math.min(((loyalty?.points || 0) / threshold) * 100, 100);
    }, [loyalty]);

    const percentToPlatinum = useMemo(() => {
        const threshold = loyalty?.[`points_to_${ELoyaltyLevel.C}`] || 0;
        if (!threshold) return 0;
        return Math.min(((loyalty?.points || 0) / threshold) * 100, 100);
    }, [loyalty]);

    const aPlanData = useMemo(() => {
        if (loyalty?.level === ELoyaltyLevel.A) {
            return loyalty?.current_plan;
        } else {
            return summary?.loyalty_settings?.plans?.[ELoyaltyLevel.A];
        }
    }, [loyalty, summary]);

    const bPlanData = useMemo(() => {
        if (loyalty?.level === ELoyaltyLevel.B) {
            return loyalty?.current_plan;
        } else {
            return summary?.loyalty_settings?.plans?.[ELoyaltyLevel.B];
        }
    }, [loyalty, summary]);

    const cPlanData = useMemo(() => {
        if (loyalty?.level === ELoyaltyLevel.C) {
            return loyalty?.current_plan;
        } else {
            return summary?.loyalty_settings?.plans?.[ELoyaltyLevel.C];
        }
    }, [loyalty, summary]);

    const data = useMemo(() => {
        const ignoredOperations = [
            // TEMP-HIDE EPS (founder 2026-06-29) — restore to re-enable: remove this line
            EFundsOperation.EPS_DEBIT_DEPOSIT,
            EFundsOperation.CARD_DEBIT_DEPOSIT,
            EFundsOperation.E_TRANSFER_DEBIT_TRANSFER,
            // GIFT_CARD_CREDIT_GIFT_CARD (8_1_13 issue) is now a loyalty-plan operation
            // (single source of truth — the value the backend charges), shown alongside
            // Redeem (8_2_14); it is no longer a product row.
            EFundsOperation.GIFT_CARD_DEBIT_RESERVE,
            EFundsOperation.GIFT_CARD_CREDIT_RESERVE
            // EFundsOperation.WALLET_CREDIT_CARD_SERVICE
        ];
        const sourcePoints = loyalty?.operation_points;
        const currentPlan = loyalty?.current_plan;
        const operations = Object.values(EFundsOperation).filter(
            (item) => !ignoredOperations.includes(item)
        );
        const operationNames = {
            [EFundsOperation.WALLET_CREDIT_TRANSFER]: 'Send money via XEPPT',
            [EFundsOperation.WALLET_DEBIT_TRANSFER]: 'Receive money via XEPPT',
            [EFundsOperation.WALLET_CREDIT_REQUEST]: 'Approve someone’s money request',
            [EFundsOperation.WALLET_DEBIT_REQUEST]: 'Request money via XEPPT (when approved)',
            [EFundsOperation.WALLET_CREDIT_CARD_DEPOSIT]: 'Load XEPPT card balance',
            [EFundsOperation.WALLET_CREDIT_CARD_SERVICE]: 'Order XEPPT card',
            [EFundsOperation.WALLET_CREDIT_PAYMENT]: 'Pay a merchant via XEPPT',
            [EFundsOperation.WALLET_DEBIT_PAYMENT]: 'Accept a payment via XEPPT',
            [EFundsOperation.PAY_BILL_CREDIT_PAYMENT]: 'Pay a bill',
            [EFundsOperation.E_TRANSFER_CREDIT_TRANSFER]: (
                <>
                    Send money via <i>Interac</i> e-Transfer®
                </>
            ),
            // [EFundsOperation.E_TRANSFER_DEBIT_TRANSFER]:
            //     'Receive money via <i>Interac</i> e-Transfer®',
            [EFundsOperation.E_TRANSFER_DEBIT_REQUEST]: (
                <>
                    Request money via <i>Interac</i> e-Transfer® (when approved)
                </>
            ),
            [EFundsOperation.EFT_CREDIT_WITHDRAWAL]: 'Withdraw to linked bank account',
            [EFundsOperation.EFT_DEBIT_DEPOSIT]: 'Load balance from linked bank account',
            [EFundsOperation.EPS_DEBIT_DEPOSIT]: 'Load balance from linked card',
            [EFundsOperation.CARD_CREDIT_WITHDRAWAL]: 'Pay with XEPPT card',
            [EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD]: 'Issue XEPPT gift card',
            [EFundsOperation.GIFT_CARD_DEBIT_GIFT_CARD_REDEEM]: 'Redeem XEPPT gift card'
        };
        return operations.map((item) => {
            return {
                source: sourcePoints?.[item as TTransactionMethodTypeAction] || 0,
                currentPlan: currentPlan?.[item as TTransactionMethodTypeAction],
                aPlan: aPlanData?.[item as TTransactionMethodTypeAction],
                bPlan: bPlanData?.[item as TTransactionMethodTypeAction],
                cPlan: cPlanData?.[item as TTransactionMethodTypeAction],
                //@ts-ignore
                name: operationNames[item as EFundsOperation],
                operation: item
            };
        });
    }, [loyalty, summary]);

    // Sum points only for the rows shown in the table (data already excludes ignoredOperations)
    const totalOperationPoints = useMemo(() => {
        return data.reduce((acc, row) => acc + (row.source || 0), 0);
    }, [data]);

    const productsData = useMemo(() => {
        const productsName = {
            [EProductName.OrderCardReplacement]: 'Replace XEPPT prepaid card',
            [EProductName.OrderPhysicCardFirstTime]: 'Request XEPPT primary card first order',
            [EProductName.OrderPhysicCardNextTimes]: 'Request XEPPT primary card',
            [EProductName.OrderSupplementaryPhysicCardFirstTime]:
                'Request XEPPT supplementary card first order',
            [EProductName.OrderSupplementaryPhysicCardNextTimes]:
                'Request XEPPT supplementary card'
            // Gift-card issue moved to the loyalty plan (key 8_1_13) — it now appears in
            // the operations tables above, not as a product. The remaining products
            // (prepaid cards) are business-only.
        };
        return Object.keys(productsName)
            .map((item) => {
                // Prepaid-card products are business-only — hide them for individuals.
                if (user?.type === EUserType.INDIVIDUAL) return undefined;
                return {
                    currentPlan: products?.find(
                        (product) => product.level === ELoyaltyLevel.A && product.name === item
                    ),
                    aPlan: products?.find(
                        (product) => product.level === ELoyaltyLevel.A && product.name === item
                    ),
                    bPlan: products?.find(
                        (product) => product.level === ELoyaltyLevel.B && product.name === item
                    ),
                    cPlan: products?.find(
                        (product) => product.level === ELoyaltyLevel.C && product.name === item
                    ),
                    //@ts-ignore
                    name: productsName[item as EProductName]
                };
            })
            .filter((item) => item !== undefined);
    }, [products, loyalty]);

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <Button
                    className={styles.back}
                    leftIcon="arrow_left"
                    variant="text-gray"
                    onClick={handleBack}>
                    {submitLocale('back')}
                </Button>
                <Typography variant="h4">My points</Typography>
            </div>
            <div className={styles.main_info_wrapper}>
                <div className={styles.item}>
                    <div className={styles.icon}>
                        <Icon name="award" />
                    </div>
                    <div className={styles.text_wrapper}>
                        <Typography variant="body3">
                            Current profile level{' '}
                            <Info
                                variant="bottom"
                                content="Your current Momentum tier, based on points earned in the previous period"
                            />
                        </Typography>
                        <Typography variant="h4">
                            {getLoyaltyLevelString(loyalty?.level)}
                        </Typography>
                    </div>
                </div>
                <div className={styles.item}>
                    <div className={styles.icon}>
                        <Icon name="coins" />
                    </div>
                    <div className={styles.text_wrapper}>
                        <Typography variant="body3">
                            Points balance{' '}
                            <Info
                                variant="bottom"
                                content="The number of points you’ve earned so far in the current period"
                            />
                        </Typography>
                        <Typography variant="h4">{fixedNumbers(loyalty?.points)}</Typography>
                    </div>
                </div>
                <div className={styles.item}>
                    <div className={styles.icon}>
                        <Icon name="calendar" />
                    </div>
                    <div className={styles.text_wrapper}>
                        <Typography variant="body3">
                            Days until period ends{' '}
                            <Info
                                variant="bottom"
                                content="The number of days remaining in your current Momentum earning period"
                            />
                        </Typography>
                        <Typography variant="h4">
                            {loyalty?.next_review_date
                                ? dateDifferenceInDays(new Date(), loyalty?.next_review_date)
                                : 0}
                        </Typography>
                    </div>
                </div>
                <div className={styles.item}>
                    <div className={styles.icon}>
                        <Icon name="history" />
                    </div>
                    <div className={styles.text_wrapper}>
                        <Typography variant="body3">
                            Total points earned{' '}
                            <Info
                                variant="bottom"
                                content="The total number of points you’ve earned since joining XEPPT"
                            />
                        </Typography>
                        <Typography variant="h4">{fixedNumbers(summary?.points)}</Typography>
                    </div>
                </div>
            </div>
            <div className={styles.data_wrapper}>
                <div className={`${styles.content_block} ${styles.diagrams_wrapper}`}>
                    <Typography variant="h5" className={styles.title}>
                        Progress by profile levels
                    </Typography>
                    <div className={styles.diagrams}>
                        <CircleDiagram size={windowWidth > 768 ? 230 : 192} progress={percentToGold}>
                            <Typography variant="h3" alignment="center">
                                {fixedNumbers(points_to_gold)}
                            </Typography>
                            <Typography variant="body4" alignment="center">
                                {/* A user AT or ABOVE Gold (Gold or Platinum) has secured it, so the
                                    Gold ring reads "maintain" — otherwise a Platinum user saw the
                                    nonsensical "0 Point remaining to Gold". Checked explicitly as
                                    B||C so an undefined level (initial load) safely reads "remaining
                                    to" rather than a misleading "maintain". */}
                                {loyalty?.level === ELoyaltyLevel.B ||
                                loyalty?.level === ELoyaltyLevel.C
                                    ? 'Points needed to maintain'
                                    : 'Point remaining to'}{' '}
                                <b>Gold</b>
                            </Typography>
                        </CircleDiagram>
                        <CircleDiagram size={windowWidth > 768 ? 230 : 192} progress={percentToPlatinum}>
                            <Typography variant="h3" alignment="center">
                                {fixedNumbers(points_to_platinum)}
                            </Typography>
                            <Typography variant="body4" alignment="center">
                                {loyalty?.level !== ELoyaltyLevel.C
                                    ? 'Point remaining to'
                                    : 'Points needed to maintain'}{' '}
                                <b>Platinum</b>
                            </Typography>
                        </CircleDiagram>
                    </div>
                    {loyalty?.next_review_date && (
                        <Typography variant="body4" className={styles.diagrams_footer}>
                            Next profile level review: <br />{' '}
                            <b>{formatDate(loyalty?.next_review_date, 'DD MMMM YYYY')}</b>
                        </Typography>
                    )}
                </div>
                <div className={`${styles.content_block} ${styles.source_block}`}>
                    <Typography variant="h5" className={styles.title}>
                        Source of points — current period
                    </Typography>
                    <div className={styles.details_table}>
                        <div className={styles.row}>
                            <Typography variant="body4" weight="semibold">
                                Source
                            </Typography>
                            <Typography variant="body4" weight="semibold">
                                Points
                            </Typography>
                        </div>
                        {data.map((item) => {
                            if (!item?.name) return null;
                            return (
                                <div className={styles.row} key={item.operation}>
                                    <Typography variant="body4">{item.name}</Typography>
                                    <Typography variant="body4">
                                        {fixedNumbers(item?.source)}
                                    </Typography>
                                </div>
                            );
                        })}
                        <div className={styles.row}>
                            <Typography variant="body4" weight="semibold">
                                TOTAL
                            </Typography>
                            <Typography variant="body4" weight="semibold">
                                {fixedNumbers(totalOperationPoints)}
                            </Typography>
                        </div>
                    </div>
                </div>
                <div className={styles.banner_desktop}>
                    <div>
                        <Typography variant="h5" alignment="center" className={styles.title}>
                            Momentum is building
                        </Typography>
                        <Typography
                            variant="body4"
                            alignment="center"
                            className={styles.description}>
                            Find out how to make it work for you
                        </Typography>
                    </div>
                    <Button
                        variant="gold"
                        size="medium"
                        onClick={() => {
                            window.open('https://xeppt.com/loyalty', '_blank');
                        }}>
                        {submitLocale('learn_more')}
                    </Button>
                    <img src={illustrationDesktop} alt="" />
                </div>
            </div>
            <div className={styles.info_wrapper}>
                <div className={styles.banner_tablet}>
                    <div>
                        <Typography variant="h5" alignment="center" className={styles.title}>
                            Momentum is building
                        </Typography>
                        <Typography
                            variant="body4"
                            alignment="center"
                            className={styles.description}>
                            Find out how to make it work for you
                        </Typography>
                    </div>
                    <Button
                        variant="gold"
                        size="medium"
                        onClick={() => {
                            window.open('https://xeppt.com/loyalty', '_blank');
                        }}>
                        {submitLocale('learn_more')}
                    </Button>
                    <img src={illustrationResponsive} alt="" />
                </div>
                <div className={styles.info_left_column}>
                    <div className={styles.invite_card}>
                        <Typography variant="h4" className={styles.title}>
                            Invite a friend, earn {loyalty?.invite_sender_points || 0} points
                        </Typography>
                        <Typography variant="body3" className={styles.description}>
                            Get rewarded for every friend who joins XEPPT
                        </Typography>
                        <Button
                            variant="primary"
                            size="medium"
                            onClick={() => navigate(routes.send_and_request_invite)}>
                            Invite to XEPPT
                        </Button>
                        <img
                            src={inviteIllustration}
                            alt=""
                            className={styles.illustration}
                        />
                    </div>
                    <div className={styles.content_block}>
                        <Typography variant="h5" className={styles.title}>
                            How to upgrade
                        </Typography>
                        {windowWidth > 360 ? (
                            <LoyaltyGraphicDesktop
                                goldPoints={
                                    summary?.loyalty_settings?.[
                                        `points_to_${ELoyaltyLevel.B}`
                                    ] || 0
                                }
                                platinumPoints={
                                    summary?.loyalty_settings?.[
                                        `points_to_${ELoyaltyLevel.C}`
                                    ] || 0
                                }
                                currentLevel={loyalty?.level}
                            />
                        ) : (
                            <LoyaltyGraphicResponsive
                                goldPoints={
                                    summary?.loyalty_settings?.[
                                        `points_to_${ELoyaltyLevel.B}`
                                    ] || 0
                                }
                                platinumPoints={
                                    summary?.loyalty_settings?.[
                                        `points_to_${ELoyaltyLevel.C}`
                                    ] || 0
                                }
                                currentLevel={loyalty?.level}
                            />
                        )}
                    </div>
                </div>
                <div className={`${styles.content_block} ${styles.earn_section}`}>
                    <Typography variant="h5" className={styles.title}>
                        How to earn points
                    </Typography>
                    <div className={styles.details_table}>
                        <div className={styles.row}>
                            <Typography variant="body4" weight="semibold">
                                Action
                            </Typography>
                            <Typography variant="body4" weight="semibold">
                                Points earned
                            </Typography>
                        </div>
                        {data.map((item) => {
                            // "How to earn points" lists only NAMED actions that actually earn
                            // points (> 0). A 0-point row is not an earning opportunity, and any
                            // operation without an operationNames entry is not a user-facing action
                            // — guard on name like the other tables. The merchant's own payment leg
                            // "Accept a payment via XEPPT" (1_2_6) is named, so it shows here when it
                            // earns points, alongside the prepaid-card product rows below.
                            if (!item.name || (item.currentPlan?.points || 0) <= 0) return null;
                            return (
                                <div className={styles.row} key={item.operation}>
                                    <Typography variant="body4">{item.name}</Typography>
                                    <Typography variant="body4">
                                        +{item.currentPlan?.points || 0} pts / $
                                        {item.currentPlan?.amount?.toFixed(2) || '0.00'}
                                    </Typography>
                                </div>
                            );
                        })}
                        {productsData.map((item) => {
                            // Show only products that actually earn points (> 0).
                            if (!item || (item.currentPlan?.points || 0) <= 0) return null;
                            return (
                                <div className={styles.row} key={item.name}>
                                    <Typography variant="body4">{item.name}</Typography>
                                    <Typography variant="body4">
                                        +{item.currentPlan?.points || 0} pts / $
                                        {item.currentPlan?.price?.toFixed(2) || '0.00'}
                                    </Typography>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            <div className={`${styles.content_block} ${styles.scroll_table}`}>
                <Typography variant="h5" className={styles.title}>
                    Enjoy lower fees as you build Momentum
                </Typography>
                {summary && (
                    <table className={styles.info_table}>
                        <thead>
                            <tr>
                                <td>
                                    <Typography variant="body4" weight="semibold">
                                        Transaction type
                                    </Typography>
                                </td>
                                <td>
                                    <Typography variant="body4" weight="semibold">
                                        Fees on Silver level, CAD
                                    </Typography>
                                </td>
                                <td>
                                    <Typography variant="body4" weight="semibold">
                                        Fees on Gold level, CAD
                                    </Typography>
                                </td>
                                <td>
                                    <Typography variant="body4" weight="semibold">
                                        Fees on Platinum level, CAD
                                    </Typography>
                                </td>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((item) => {
                                // Hide rows with no transaction-type label (any operation without an
                                // operationNames entry). The merchant's "Accept a payment via XEPPT"
                                // leg (1_2_6) is named, so its sale fee shows here across tiers; the
                                // all-free filter below still hides rows that are $0 on every tier.
                                if (!item.name) return null;
                                // Hide rows that are FREE on every tier — a 0/0/0 row carries no
                                // momentum-fee information. A row free on only some tiers is kept
                                // (its free tiers render "Free" in the cells below).
                                if (
                                    (item.aPlan?.percent || 0) === 0 &&
                                    (item.aPlan?.flat || 0) === 0 &&
                                    (item.bPlan?.percent || 0) === 0 &&
                                    (item.bPlan?.flat || 0) === 0 &&
                                    (item.cPlan?.percent || 0) === 0 &&
                                    (item.cPlan?.flat || 0) === 0
                                )
                                    return null;
                                // Gift-card ISSUE fee is FLAT-ONLY (8_1_13) — suppress its percent
                                // so it renders "$2.00", never "2% + $2.00".
                                const isIssueRow =
                                    item.operation === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD;
                                return (
                                    <tr key={item.operation}>
                                        <td>
                                            <Typography variant="body4">{item.name}</Typography>{' '}
                                        </td>
                                        <td>
                                            <Typography variant="body4">
                                                {formatFeeRate({
                                                    percent: isIssueRow
                                                        ? undefined
                                                        : item.aPlan?.percent,
                                                    flat: item.aPlan?.flat,
                                                    renderFlat: (f) => `$${f.toFixed(2)}`
                                                }) || 'Free'}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography variant="body4">
                                                {formatFeeRate({
                                                    percent: isIssueRow
                                                        ? undefined
                                                        : item.bPlan?.percent,
                                                    flat: item.bPlan?.flat,
                                                    renderFlat: (f) => `$${f.toFixed(2)}`
                                                }) || 'Free'}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography variant="body4">
                                                {formatFeeRate({
                                                    percent: isIssueRow
                                                        ? undefined
                                                        : item.cPlan?.percent,
                                                    flat: item.cPlan?.flat,
                                                    renderFlat: (f) => `$${f.toFixed(2)}`
                                                }) || 'Free'}
                                            </Typography>
                                        </td>
                                    </tr>
                                );
                            })}
                            {productsData.map((item) => {
                                if (!item) return null;
                                // Hide products free on every tier (e.g. "Replace XEPPT prepaid
                                // card" 0/0/0) — a $0/$0/$0 row carries no momentum info.
                                if (
                                    (item.aPlan?.price || 0) === 0 &&
                                    (item.bPlan?.price || 0) === 0 &&
                                    (item.cPlan?.price || 0) === 0
                                )
                                    return null;
                                return (
                                    <tr key={item.name}>
                                        <td>
                                            <Typography variant="body4">
                                                {item.name}
                                            </Typography>{' '}
                                        </td>
                                        <td>
                                            <Typography variant="body4">
                                                ${item.aPlan?.price?.toFixed(2)}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography variant="body4">
                                                ${item.bPlan?.price?.toFixed(2)}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography variant="body4">
                                                ${item.cPlan?.price?.toFixed(2)}
                                            </Typography>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default LoyaltyLayout;
